import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MaskLine, Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { getProducts, getCategories } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

export default function Products() {
  const { t, lang } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("cat") || "all";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
    getCategories().then(setCats).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.category === active)),
    [products, active]
  );

  const setCat = (slug) => {
    if (slug === "all") setSearchParams({});
    else setSearchParams({ cat: slug });
  };

  const filters = [{ slug: "all", name: t("products.allProducts") }, ...cats];

  const currentSeo = useMemo(() => {
    if (active && active !== "all" && SEO_CONFIG.categories[active]) {
      return SEO_CONFIG.categories[active];
    }
    return SEO_CONFIG.products;
  }, [active]);

  const productsSchema = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": currentSeo.title,
      "description": currentSeo.description,
      "url": currentSeo.canonical,
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": filtered.map((p, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "name": p.name,
          "url": `https://www.ashlarbuildchem.com/products/${p.slug}`,
        })),
      },
    };
  }, [currentSeo, filtered]);

  return (
    <div className="pt-20" data-testid="products-page">
      <SEO {...currentSeo} schema={productsSchema} />
      {/* Header */}
      <section className="mx-auto max-w-[1600px] px-6 pb-16 pt-20 md:px-10 md:pb-20 md:pt-28">
        <span className="label text-primary">{t("products.catalogue")}</span>
        <h1
          className={`mt-6 font-display text-4xl font-bold sm:text-6xl md:text-8xl ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.85] tracking-tighter"
          }`}
        >
          <MaskLine delay={0.05}>{t("products.title")}</MaskLine>
        </h1>
        <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
          {t("products.intro")}
        </p>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1600px] px-6 pb-32 md:px-10">
        <div className="grid gap-12 lg:grid-cols-[240px_1fr]">
          {/* Filters */}
          <aside className="min-w-0 lg:sticky lg:top-28 lg:h-fit">
            <span className="label mb-5 hidden text-muted-foreground lg:block">{t("products.filter")}</span>
            <div className="no-scrollbar -mx-6 flex gap-2 overflow-x-auto px-6 pb-2 lg:mx-0 lg:flex-col lg:gap-0 lg:overflow-visible lg:px-0 lg:pb-0">
              {filters.map((f) => (
                <button
                  key={f.slug}
                  onClick={() => setCat(f.slug)}
                  data-testid={`filter-${f.slug}`}
                  className={`group flex shrink-0 items-center justify-between border px-5 py-3 text-left transition-colors duration-200 lg:border-x-0 lg:border-t-0 lg:border-b lg:border-black/10 lg:px-0 lg:py-4 ${
                    active === f.slug
                      ? "border-primary bg-primary text-primary-foreground lg:bg-transparent lg:text-primary"
                      : "border-black/15 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span className="whitespace-nowrap font-display text-sm font-medium lg:text-base">{f.name}</span>
                  <span className={`ml-3 hidden text-xs lg:inline ${active === f.slug ? "text-primary" : "text-black/30"}`}>
                    {f.slug === "all" ? products.length : products.filter((p) => p.category === f.slug).length}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          {/* Grid */}
          <div className="min-w-0">
            <div className="mb-6 flex items-center justify-between">
              <span className="label text-muted-foreground" data-testid="products-count">
                {filtered.length} {filtered.length === 1 ? t("products.product") : t("products.productsCount")}
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3"
              >
                {filtered.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 0.05}>
                    <ProductCard product={p} index={i} />
                  </Reveal>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
}
